import {makeStyles} from "@material-ui/core/styles"
import React, {useEffect, useMemo, useState} from "react"
import {withHistory} from "slate-history"
import {Slate, withReact} from "slate-react"
import {createEditor} from "slate"
import {Box, FormControl, FormHelperText} from "@material-ui/core"
import clsx from "clsx"
import RichTextEditor from "./RichTextEditor"
import * as PropTypes from "prop-types"
import lang from "../../core/lang"
import UserEvent from "../../core/UserEvent"
import RichTextCanvas from "./RichTextCanvas"

const EMPTY = {
    type: 'rtx',
    children: [
        {
            type: 'paragraph',
            children: [{ text: '' }]
        }
    ]
}

const parse = any => {
    if(lang.isEmpty(any)) {
        return EMPTY
    }

    if(lang.isObject(any)) {
        return any
    }

    let value
    let isValid = false
    try {
        if(lang.isString(any) && isNaN(parseFloat(any))) {
            value = JSON.parse(any)
            isValid = true
        }
    } catch (e) {
        isValid = false
    }

    if(!isValid) {
        const text = String(any)
        const lines = text.split('\n')
        const children = lines.map(line => ({
            type: 'paragraph',
            children: [{ text: line }],
        }))
        value = {
            type: 'rtx',
            children
        }
    }

    return value
}

const stringify = value => {
    if(lang.isString(value)) {
        return value
    }
    if(lang.isObject(value)) {
        return JSON.stringify(value)
    } else {
        return String(value)
    }
}

const useStyles = makeStyles(theme => ({
    root: {
        height: props => props.height,
        width: props => props.width,
        fontSize: '1rem'
    },
    canvas: {
        height: '100%',
        overflowY: 'auto'
    }
}))

function RichText(props) {
    const {value, defaultValue, className, style = {}, fullHeight, fullWidth, autofocus, placeholder, editable} = props
    const classes = useStyles()
    const isControlled = value !== undefined
    // Create a Slate editor object that won't change across renders.
    const editor = useMemo(() => RichTextEditor.withInlines(withHistory(withReact(createEditor()))), [])
    const width = fullWidth ? '100%' : props.width
    const height = fullHeight ? '100%' : props.height
    const [initialized, setInitialized] = useState(false)

    const handleChange = value => {
        const content = {
            type: 'rtx',
            children: value
        }
        props.onChange && props.onChange(new UserEvent({value: content}))
    }

    useEffect(() => {
        setInitialized(true)
    }, [])

    useEffect(() => {
        if (!isControlled || !initialized) {
            return
        }

        const rtx = parse(value)
        if(stringify(rtx.children) !== stringify(editor.children)) {
            RichTextEditor.reset(editor, rtx.children)
        }
    }, [value, editor, isControlled, initialized])

    const initialValue = parse(isControlled ? value : defaultValue)

    return (
        <Box className={clsx('appfuse-RichText', className, classes.root)} style={style} >
            <Slate editor={editor} initialValue={initialValue.children} onChange={handleChange} >
                <Box className={classes.canvas}>
                    <RichTextCanvas readOnly={!editable} placeholder={placeholder} autofocus={autofocus} />
                </Box>
            </Slate>
        </Box>
    )
}

RichText.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onChange: PropTypes.func,
    name: PropTypes.string,
    editable: PropTypes.bool,
    placeholder: PropTypes.string,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    height: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    autofocus: PropTypes.bool
}

RichText.defaultProps = {
    editable: false
}

export default RichText